/**
 * BreachReportPage
 * Route: /admin/breach/report
 *
 * Breach intake form for DPO/admin users.
 * Required by DPDP Act 2023 Section 8(6).
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  AlertTriangle,
  Lock,
  Users,
  CheckCircle,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { reportBreach } from "@/services/dpdpApi";

const BREACH_TYPES = [
  { id: "unauthorized_access", label: "Unauthorized Access", description: "Unauthorized login or account compromise" },
  { id: "data_leak", label: "Data Leak", description: "Unintended exposure of personal data" },
  { id: "system_breach", label: "System Breach", description: "Infrastructure or application vulnerability exploited" },
  { id: "insider_threat", label: "Insider Threat", description: "Malicious or negligent action by internal personnel" },
  { id: "third_party", label: "Third-Party Breach", description: "Breach via a vendor or partner" },
  { id: "physical", label: "Physical Breach", description: "Physical theft or unauthorized access to premises" },
  { id: "other", label: "Other", description: "Other type of breach" },
];

const SEVERITY_LEVELS = [
  { id: "low", label: "Low", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200", description: "Limited scope, no sensitive data" },
  { id: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200", description: "Moderate impact, some sensitive data" },
  { id: "high", label: "High", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200", description: "Significant impact, personal data at risk" },
  { id: "critical", label: "Critical", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200", description: "Widespread, sensitive personal data exposed" },
];

const DATA_CATEGORIES = [
  "name", "email", "phone", "mobile", "address", "dob",
  "aadhaar", "pan", "bank_account", "password", "biometric",
  "health", "financial", "location", "psychometric", "employment",
  "family", "photo", "voice", "chat_history", "device_id", "ip_address", "cookies",
];

export default function BreachReportPage() {
  const navigate = useNavigate();

  const [breachType, setBreachType] = useState("");
  const [severity, setSeverity] = useState("");
  const [description, setDescription] = useState("");
  const [detectedAt, setDetectedAt] = useState("");
  const [affectedCategories, setAffectedCategories] = useState([]);
  const [estimatedUsers, setEstimatedUsers] = useState("");
  const [containmentSteps, setContainmentSteps] = useState("");
  const [reportedBy, setReportedBy] = useState("employee");
  const [sourceDetails, setSourceDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [breachId, setBreachId] = useState("");

  const toggleCategory = (cat) => {
    setAffectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!breachType || !severity || !description || affectedCategories.length === 0 || !detectedAt || !containmentSteps.trim()) {
      toast.error("Please fill all required fields");
      return;
    }
    if (description.length < 20) {
      toast.error("Description must be at least 20 characters");
      return;
    }
    const steps = containmentSteps.split("\n").filter((s) => s.trim());
    if (steps.length === 0) {
      toast.error("Please list at least one containment step");
      return;
    }

    setSubmitting(true);
    try {
      const result = await reportBreach({
        breach_type: breachType,
        severity,
        description,
        detected_at: new Date(detectedAt).toISOString(),
        affected_data_categories: affectedCategories,
        estimated_affected_users: parseInt(estimatedUsers) || 0,
        containment_steps: steps,
        reported_by: reportedBy,
        source_details: sourceDetails || null,
      });
      setBreachId(result.breach_id);
      setSubmitted(true);
      toast.success("Breach record created. 72-hour DPB notification clock has started.");
    } catch (err) {
      toast.error(err.message || "Failed to create breach record");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Breach Record Created</CardTitle>
            <CardDescription>
              The 72-hour DPB notification deadline has started.
              Navigate to the investigation dashboard to track and manage this breach.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Breach ID</p>
              <p className="font-mono text-sm font-semibold break-all">{breachId}</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/admin/breach")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => navigate(`/admin/breach/${breachId}`)}
              >
                Investigate
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950/20 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-display text-lg font-bold text-white flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-400" />
              Breach Intake Form
            </h1>
            <p className="text-xs text-white/50">DPDP Act 2023 — Section 8(6)</p>
          </div>
          <Badge variant="outline" className="ml-auto border-red-500/50 text-red-400">
            DPO / Admin Only
          </Badge>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Deadline notice */}
        <div className="mb-6 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              72-Hour Notification Deadline
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Under Section 8(6) of the DPDP Act, the Data Protection Board must be notified
              within 72 hours of detecting a personal data breach. This form starts that clock.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Breach Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="h-4 w-4 text-indigo-400" />
                Breach Classification
              </CardTitle>
              <CardDescription>What type of breach occurred?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {BREACH_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setBreachType(type.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      breachType === type.id
                        ? "border-red-500 bg-red-500/10"
                        : "border-white/10 bg-white/5 hover:border-white/30"
                    }`}
                  >
                    <p className="font-medium text-sm text-white">{type.label}</p>
                    <p className="text-xs text-white/40 mt-0.5">{type.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Severity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                Severity Assessment
              </CardTitle>
              <CardDescription>How severe is this breach?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {SEVERITY_LEVELS.map((level) => (
                  <button
                    key={level.id}
                    type="button"
                    onClick={() => setSeverity(level.id)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      severity === level.id
                        ? "border-white bg-white/10"
                        : "border-white/10 bg-white/5 hover:border-white/30"
                    }`}
                  >
                    <p className={`text-sm font-bold ${severity === level.id ? "text-white" : "text-white/70"}`}>
                      {level.label}
                    </p>
                    <p className="text-xs text-white/40 mt-1 hidden sm:block">{level.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Description & Detection Time */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Breach Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Detection Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={detectedAt}
                    onChange={(e) => setDetectedAt(e.target.value)}
                    className="mt-1 bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Reported By</Label>
                  <select
                    value={reportedBy}
                    onChange={(e) => setReportedBy(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-white/20 bg-white/10 text-white"
                  >
                    <option value="system">Automated System</option>
                    <option value="employee">Employee</option>
                    <option value="user">Affected User</option>
                    <option value="third_party">Third Party</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-white/70">Description * (min. 20 chars)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what happened, how it was discovered, and the initial impact assessment..."
                  className="mt-1 h-24 bg-white/10 border-white/20 text-white resize-none"
                  minLength={20}
                />
                <p className="text-xs text-white/40 mt-1">{description.length} characters</p>
              </div>

              <div>
                <Label className="text-white/70">Source / Discovery Details</Label>
                <Input
                  value={sourceDetails}
                  onChange={(e) => setSourceDetails(e.target.value)}
                  placeholder="e.g., SIEM alert #1234, user complaint, audit finding..."
                  className="mt-1 bg-white/10 border-white/20 text-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Affected Data Categories */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-400" />
                Affected Data Categories *
              </CardTitle>
              <CardDescription>
                Select all categories of personal data that may have been compromised
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {DATA_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                      affectedCategories.includes(cat)
                        ? "border-red-500 bg-red-500/20 text-red-300"
                        : "border-white/10 bg-white/5 text-white/70 hover:border-white/30"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {affectedCategories.length > 0 && (
                <p className="text-xs text-white/40 mt-2">
                  {affectedCategories.length} category(ies) selected
                </p>
              )}
            </CardContent>
          </Card>

          {/* Estimated Users & Containment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Impact & Containment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white/70">Estimated Affected Users</Label>
                <Input
                  type="number"
                  min="0"
                  value={estimatedUsers}
                  onChange={(e) => setEstimatedUsers(e.target.value)}
                  placeholder="Approximate number of data principals affected"
                  className="mt-1 bg-white/10 border-white/20 text-white"
                />
              </div>

              <div>
                <Label className="text-white/70">Containment Steps * (one per line)</Label>
                <Textarea
                  value={containmentSteps}
                  onChange={(e) => setContainmentSteps(e.target.value)}
                  placeholder={"Revoked compromised credentials\nIsolated affected database\nReset all session tokens"}
                  className="mt-1 h-28 bg-white/10 border-white/20 text-white resize-none font-mono text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-red-600 hover:bg-red-700 h-12 text-base"
              disabled={submitting || !breachType || !severity}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <ShieldAlert className="h-4 w-4 mr-2" />
                  Submit Breach Report
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
